import { Button, Col, Modal, Row, Space, Tooltip, Typography } from 'antd';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { PaymentAccountDoctor } from 'gql/profile/__generated__/PaymentAccountDoctor';
import { GET_PAYMENT_ACCOUNT } from 'gql/profile/queries';

import { InvoiceTemplateFields, UploadInvoice } from './components';
import { Loading } from 'components/ui';
import PaymentFields, {
  PaymentFieldsRef
} from 'components/shared/PaymentFields';

enum Variants {
  file,
  template
}

interface IProps {
  appointmentId: string;
}

const CreateInvoiceModal: FC<IProps> = ({ appointmentId }) => {
  const [variant, setVariant] = useState<Variants | null>(null);
  const [isVisible, setVisible] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const ref = useRef<PaymentFieldsRef>(null);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<PaymentAccountDoctor>(
    GET_PAYMENT_ACCOUNT,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !isVisible
    }
  );

  const hasPaymentInfo = !!data?.paymentAccountDoctor?.data?.signature;

  const onClose = () => {
    setVisible(false);
    setVariant(null);
  };

  const onPaymentDetailsSubmitted = () => {
    setSubmitting(false);
  };

  const onSubmitPaymentDetails = () => {
    setSubmitting(true);
    ref.current?.submit();
  };

  const renderContent = () => {
    if (!hasPaymentInfo) {
      return (
        <>
          <PaymentFields
            ref={ref}
            onCompleted={() => refetch()}
            onError={onPaymentDetailsSubmitted}
          />
          <Button
            block
            type="primary"
            loading={isSubmitting}
            onClick={onSubmitPaymentDetails}
          >
            {t('common.submit')}
          </Button>
        </>
      );
    }

    switch (variant) {
      case Variants.file:
        return (
          <UploadInvoice appointmentId={appointmentId} onClose={onClose} />
        );
      case Variants.template:
        return (
          <InvoiceTemplateFields
            appointmentId={appointmentId}
            onClose={onClose}
          />
        );
      default:
        return (
          <div>
            <Typography.Paragraph>
              {t('appointments_details.select_your_preferred')}
            </Typography.Paragraph>
            <Row gutter={[16, 0]} style={{ paddingTop: 16 }}>
              <Col flex={1}>
                <Button
                  block
                  size="large"
                  type="link"
                  icon={
                    <Tooltip
                      title={t('appointments_details.use_template_description')}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  }
                  onClick={() => setVariant(Variants.template)}
                >
                  {t('appointments_details.use_template')}
                </Button>
              </Col>
              <Col flex={1}>
                <Button
                  block
                  icon={
                    <Tooltip
                      title={t(
                        'appointments_details.upload_invoice_file_description'
                      )}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  }
                  size="large"
                  type="link"
                  onClick={() => setVariant(Variants.file)}
                >
                  {t('appointments_details.upload_invoice_file')}
                </Button>
              </Col>
            </Row>
          </div>
        );
    }
  };

  const renderTitle = () => {
    if (!hasPaymentInfo) {
      return t('invoices.fill_in_payment');
    }

    switch (variant) {
      case Variants.file:
        return (
          <Space>
            <Button
              onClick={() => setVariant(null)}
              icon={<ArrowLeftOutlined />}
              type="text"
            />
            <Typography.Text>
              {t('appointments_details.upload_invoice_file')}
            </Typography.Text>
          </Space>
        );
      case Variants.template:
        return (
          <Space>
            <Button
              onClick={() => setVariant(null)}
              icon={<ArrowLeftOutlined />}
              type="text"
            />
            <Typography.Text>
              {t('appointments_details.use_template')}
            </Typography.Text>
          </Space>
        );
      default:
        return t('appointments_details.invoice_submission_options');
    }
  };

  return (
    <>
      <Button onClick={() => setVisible(true)} type="primary" size="large">
        {t('appointments_details.send_invoice')}
      </Button>
      <Modal
        open={isVisible}
        title={renderTitle()}
        onCancel={onClose}
        footer={null}
      >
        {loading ? <Loading type="static" /> : renderContent()}
      </Modal>
    </>
  );
};

export default CreateInvoiceModal;
